import React from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API } from "@/lib/api";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldAlert, Phone, Siren, Flame, Ambulance, Hotel, Plane, Ticket, Compass, BedDouble, ChefHat, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import LiveFood from "@/components/LiveFood";
import { KIND_LABEL } from "@/lib/recipe";
import { bookingLink, hotellookLink, flightsLink, toursLink, ticketsLink } from "@/lib/affiliates";
import { DEST_IATA, ORIGINS } from "@/lib/tripProfile";
import { isPlayApp, hasPlayBilling } from "@/lib/playBilling";
import { trackEvent } from "@/lib/analytics";

const pick = (o, lang) => (o && (o[lang] || o.en)) || "";
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const ll = (s) => `${s.lat},${s.lng}`;

const checkEmbed = async () => (await axios.get(`${API}/maps/embed-check`, { params: { key: MAPS_KEY } })).data.ok;

function StopList({ stops, lang }) {
  return (
    <ol className="rounded-2xl border border-stoneLine bg-card divide-y divide-stoneLine" data-testid="trip-map-fallback">
      {stops.map((s, i) => (
        <li key={s.name.en} className="flex items-center gap-3 px-4 py-2.5">
          <span className="w-7 h-7 rounded-full bg-ink text-sand text-xs font-bold flex items-center justify-center shrink-0 stat-number">{i + 1}</span>
          <a href={`https://www.google.com/maps/search/?api=1&query=${ll(s)}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-ink hover:underline flex-1">{pick(s.name, lang)}</a>
        </li>
      ))}
    </ol>
  );
}

export function TripMap({ day, lang, mapLang }) {
  const { t } = useLang();
  const stops = day.stops;
  const { data: embedOk } = useQuery({ queryKey: ["maps-embed-ok"], queryFn: checkEmbed, enabled: Boolean(MAPS_KEY), staleTime: 30 * 60 * 1000, retry: false });
  const origin = ll(stops[0]); const dest = ll(stops[stops.length - 1]);
  const way = stops.slice(1, -1).map(ll).join("|");
  const open = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${way ? `&waypoints=${way}` : ""}&travelmode=driving`;
  const embed = MAPS_KEY && embedOk !== false ? `https://www.google.com/maps/embed/v1/directions?key=${MAPS_KEY}&origin=${origin}&destination=${dest}${way ? `&waypoints=${way}` : ""}&mode=driving&language=${mapLang || lang}` : null;
  return (
    <div className="space-y-3" data-testid="trip-map">
      {embed ? <iframe title="map" src={embed} className="w-full h-[320px] sm:h-[420px] rounded-2xl border border-stoneLine" loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" data-testid="trip-map-embed" />
        : <StopList stops={stops} lang={lang} />}
      <a href={open} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("trip_open_maps", { city: day.city })} className="inline-flex items-center gap-1.5 rounded-full bg-ink text-sand px-4 py-2 text-sm font-semibold hover:bg-ochre transition-colors" data-testid="trip-open-maps">
        <ExternalLink className="w-4 h-4" /> {t("trip_open_maps")}
      </a>
    </div>
  );
}

const LEVEL_COLOR = { relaxed: "bg-oasis/15 text-oasis", moderate: "bg-ochre/15 text-ochre", strict: "bg-red-600/10 text-red-700" };

export function TripWarnings({ trip, lang }) {
  const { t } = useLang();
  const sos = trip.sos;
  const rows = [["police", Siren, sos.police], ["ambulance", Ambulance, sos.ambulance], ["fire", Flame, sos.fire], ["tourist_police", ShieldAlert, sos.tourist_police]].filter((r) => r[2]);
  return (
    <div className="space-y-4" data-testid="trip-warnings">
      <div className="rounded-2xl border border-stoneLine bg-card p-4">
        <span className={`inline-block rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${LEVEL_COLOR[trip.level]}`} data-testid="trip-dress-level">{t(`dress_${trip.level}`)}</span>
        <p className="text-sm leading-relaxed text-ink mt-3" data-testid="trip-warning-text">{pick(trip.warning, lang)}</p>
      </div>
      <div className="rounded-2xl border border-stoneLine bg-card p-4" data-testid="trip-sos">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("sos_title")}</div>
        {sos.unified && <a href={`tel:${sos.unified}`} className="flex items-center justify-between rounded-xl bg-red-600 text-white px-4 py-3 mb-2" data-testid="sos-unified"><span className="inline-flex items-center gap-2 text-sm font-semibold"><Phone className="w-4 h-4" /> {t("sos_unified")}</span><span className="font-display font-bold text-2xl stat-number">{sos.unified}</span></a>}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {rows.map(([k, Icon, num]) => <a key={k} href={`tel:${num}`} className="rounded-xl border border-stoneLine p-3 hover:border-ochre/50 transition-colors" data-testid={`sos-${k}`}><Icon className="w-4 h-4 text-ochre" /><div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t(`sos_${k}`)}</div><div className="font-display font-bold text-xl text-ink stat-number">{num}</div></a>)}
        </div>
        {sos.note && <p className="text-xs text-muted-foreground mt-2">{pick(sos.note, lang)}</p>}
      </div>
    </div>
  );
}

export function TripFood({ country, lang }) {
  const { t } = useLang();
  const cuisine = country?.travel_guide?.cuisine || {};
  const kinds = ["dish", "dessert", "street"].filter((k) => cuisine[k]);
  const neutral = isPlayApp() && !hasPlayBilling();
  return (
    <div className="space-y-4" data-testid="trip-food">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {kinds.map((k) => { const f = cuisine[k]; return (
          <article key={k} className="rounded-2xl overflow-hidden border border-stoneLine bg-card" data-testid={`trip-food-${k}`}>
            {f.image && <LiveFood food={f} kind={k} alt={pick(f.name, lang)} className="aspect-[16/10]" />}
            <div className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(KIND_LABEL[k])}</div><div className="font-semibold text-ink">{pick(f.name, lang)}</div><p className="text-xs text-muted-foreground mt-1 leading-snug">{pick(f.desc, lang)}</p></div>
          </article>); })}
      </div>
      <Link to="/ricettario" onClick={() => trackEvent("trip_upsell_click", { code: country?.code })} className="flex items-center gap-3 rounded-2xl bg-ink text-sand p-4 hover:bg-ochre transition-colors" data-testid="trip-cookbook-upsell">
        <span className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0"><ChefHat className="w-6 h-6" /></span>
        <span className="text-sm leading-snug">{t(neutral ? "trip_upsell_neutral" : "trip_upsell")}</span>
      </Link>
    </div>
  );
}

export function TripBook({ trip, profile, city, lang }) {
  const { t } = useLang();
  const origin = ORIGINS.find((o) => o.code === profile?.origin);
  const opt = { lang, currency: origin?.currency || "EUR" };
  const items = [
    ["hotels", Hotel, bookingLink(city, opt), "Booking.com"],
    ["compare", BedDouble, hotellookLink(city, opt), "Hotellook"],
    ["flights", Plane, flightsLink(origin?.iata, DEST_IATA[trip.code], opt), "Aviasales"],
    ["tours", Compass, toursLink(city, opt), "GetYourGuide"],
    ["tickets", Ticket, ticketsLink(city, opt), "Tiqets"],
  ];
  return (
    <div className="space-y-3" data-testid="trip-book">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map(([k, Icon, href, brand]) => (
          <a key={k} href={href} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackEvent("affiliate_click", { kind: k, city })} className="group flex items-center gap-3 rounded-2xl border border-stoneLine bg-card p-4 hover:border-ochre/60 hover:shadow-md transition-all" data-testid={`book-${k}`}>
            <span className="w-11 h-11 rounded-2xl bg-ochre/10 text-ochre flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
            <span className="flex-1 min-w-0"><span className="block font-semibold text-ink">{t(`book_${k}`).replace("{city}", city)}</span><span className="block text-[11px] text-muted-foreground">{brand} · {t("book_partner")}</span></span>
            <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-ochre" />
          </a>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5" data-testid="book-disclosure"><Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {t("book_disclosure")}</p>
    </div>
  );
}
